import type { DataGap, RawProfilePoint } from '@/types/profile';

/**
 * Fills detected gaps in the primary (trimmed) profile using a companion
 * "detail" file — e.g. a rougher-axis turning program that happens to have
 * recorded the region the main finishing program skipped (a knob bulb, a
 * cup rim). This is explicitly a reconstruction, not verified machine data,
 * and every point it produces is tagged `reconstructed: true` so the rest
 * of the app can flag it distinctly rather than presenting it as real.
 *
 * How it works, per gap:
 *  1. Find the companion file's points that fall strictly inside the gap's
 *     position range (its own lead-in/out and other data outside the gap
 *     are ignored entirely).
 *  2. Interpolate the companion curve's radius exactly at the gap's two
 *     boundary positions, then compute a linear rescale (scale + offset)
 *     that maps those two companion values onto the primary file's ACTUAL
 *     boundary radii. This anchors the reconstruction to real, trusted
 *     data on both sides — only the companion's relative shape (where its
 *     bumps and curves are) gets borrowed, never its absolute size.
 *  3. Splice the rescaled companion points into the primary profile,
 *     replacing the gap.
 *
 * If a gap has no usable companion data (nothing recorded in that position
 * range in the second file either), it's left unresolved and reported as
 * such — this never fabricates geometry from nothing.
 */

const MIN_POINTS_TO_FILL = 2;

/** Linear interpolation of a sorted points array's radius at an arbitrary position. */
function interpolateRadiusAt(sorted: RawProfilePoint[], position: number): number | null {
  if (sorted.length === 0) return null;
  if (position <= sorted[0].position) return sorted[0].radius;
  if (position >= sorted[sorted.length - 1].position) return sorted[sorted.length - 1].radius;

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (position >= a.position && position <= b.position) {
      if (b.position === a.position) return a.radius;
      const t = (position - a.position) / (b.position - a.position);
      return a.radius + t * (b.radius - a.radius);
    }
  }
  return null;
}

export interface GapFillOutcome {
  /** The primary profile with resolvable gaps spliced in (sorted by position). */
  points: RawProfilePoint[];
  /** Gaps that were successfully filled, with `filled: true` set. */
  filledGaps: DataGap[];
  /** Gaps that had no usable companion data and remain open. */
  unresolvedGaps: DataGap[];
}

export function fillGapsFromDetail(
  primaryPoints: RawProfilePoint[],
  gaps: DataGap[],
  detailPoints: RawProfilePoint[],
): GapFillOutcome {
  const sortedPrimary = [...primaryPoints].sort((a, b) => a.position - b.position);
  const sortedDetail = [...detailPoints].sort((a, b) => a.position - b.position);

  const filledGaps: DataGap[] = [];
  const unresolvedGaps: DataGap[] = [];
  const insertions: RawProfilePoint[] = [];

  for (const gap of gaps) {
    const inside = sortedDetail.filter(
      (p) => p.position > gap.fromPosition && p.position < gap.toPosition,
    );

    if (inside.length < MIN_POINTS_TO_FILL) {
      unresolvedGaps.push(gap);
      continue;
    }

    const detailAtFrom = interpolateRadiusAt(sortedDetail, gap.fromPosition);
    const detailAtTo = interpolateRadiusAt(sortedDetail, gap.toPosition);

    if (detailAtFrom === null || detailAtTo === null || detailAtFrom === detailAtTo) {
      unresolvedGaps.push(gap);
      continue;
    }

    // Rescale the companion curve so its boundary values exactly match the
    // primary file's real, trusted boundary radii.
    const scale = (gap.toRadius - gap.fromRadius) / (detailAtTo - detailAtFrom);
    const offset = gap.fromRadius - scale * detailAtFrom;

    const reconstructedSegment: RawProfilePoint[] = inside.map((p) => ({
      index: -1, // synthetic — not a real row from any source file
      position: p.position,
      radius: scale * p.radius + offset,
      reconstructed: true,
    }));

    // Sanity-check the result before trusting it. The .C and companion files
    // may not share the same position reference (different axis/program) —
    // when they don't, this rescale can produce nonsense (negative radius,
    // wild overshoot) rather than a real curve. Reject rather than insert.
    const boundaryMax = Math.max(gap.fromRadius, gap.toRadius);
    const boundaryMin = Math.min(gap.fromRadius, gap.toRadius);
    const hasNegative = reconstructedSegment.some((p) => p.radius <= 0);
    const hasWildOvershoot = reconstructedSegment.some(
      (p) => p.radius > boundaryMax * 3 || p.radius < boundaryMin / 3,
    );

    if (hasNegative || hasWildOvershoot) {
      unresolvedGaps.push(gap);
      continue;
    }

    insertions.push(...reconstructedSegment);
    filledGaps.push({ ...gap, filled: true });
  }

  const merged = [...sortedPrimary, ...insertions].sort((a, b) => a.position - b.position);
  const reindexed = merged.map((p, i) => ({ ...p, index: i }));

  return { points: reindexed, filledGaps, unresolvedGaps };
}
